import pseudoTranslate from '@nike/i18n-pseudo';
import IntlMessageFormat from 'intl-messageformat';
import { logWarn } from '../utils/log';
// A list of tags that only exist in development mode
const DEVELOPMENT_TAGS = ['pseudo', 'primary-value', 'string-keys'];
/**
 * This accepts a given string and other parameters and processes any ICU message data
 * @param {string} string - The ICU message string
 * @param {string|array} langTag - The language to localize the ICU data into
 * @param {object} icuTokens - An object including the values to inject into the ICU message
 * @param {object} icuOptions - An object of options to pass along to the IntlMessageFormat module
 * @param {string} stringKey - the stringKey to which the ICU message string is associated
 * @return {string} Return
 */
export const processICUString = (string, langTag, icuTokens, icuOptions, stringKey) => {
    const msg = new IntlMessageFormat(string, langTag, icuOptions);
    try {
        return msg.format(icuTokens);
    }
    catch (e) {
        logWarn('Error in processing ICU string - please check for mismatching ICU tokens and '
            + `options for stringKey '${stringKey}' in language '${langTag}'`);
        return stringKey;
    }
};
/**
 * Handles all of the logic to receive a stringKey / langTag and outputs the relevant translation
 *
 * @param {string} stringKey - The key associated with this specific string
 * @param {string} langTag - This is for dev env only. <'pseudo', 'primary-value', 'string-keys'>
 * @param {string} originalString - The string's Primary Value, mostly used during development
 * @param {string} description - The string's description, currently just being used to
 *                               verify it exists
 * @param {object} translations - The translations object for a specific language
 * @return {type} Return
 */
export default function getTranslation(stringKey, langTag, originalString, description, translations, icuTokens = null, icuOptions = {}) {
    // Bail if we don't have a stringKey
    if (!stringKey) {
        throw new Error('You must pass the \'stringKey\' param');
    }
    // Bail if we don't have a langTag
    if (!langTag) {
        throw new Error('You must pass the \'langTag\' param');
    }
    // Heavily incourage the use of descriptions
    if (!description) {
        logWarn(`It's highly recommended you provide a description for the string ${stringKey}`);
    }
    // We're currently using a development locale
    if (DEVELOPMENT_TAGS.includes(langTag)) {
        // Don't do any of the below logic if we just need to output the string's key
        if (langTag === 'string-keys') {
            return stringKey;
        }
        // Getting the primary value or a default string
        let primaryValueStr = originalString || '[No String Provided]';
        // Process the ICU content if we were given tokens
        if (icuTokens !== null) {
            primaryValueStr = processICUString(primaryValueStr, langTag, icuTokens, icuOptions, stringKey);
        }
        // Apply pseudo translation to the original string passed
        if (langTag === 'pseudo') {
            return pseudoTranslate(primaryValueStr);
        }
        // Output the primary value as it was given to us
        return primaryValueStr;
    }
    if (translations[stringKey] === undefined) {
        logWarn(`The string ${stringKey} doesn't exist in the provided translation `
            + 'file. We\'ve wrapped the string in ≪≫ characters so you don\'t mistakenly '
            + `think you're viewing the ${langTag} translation value when you're in fact `
            + 'viewing the primary value. See https://confluence.nike.com/display/G11N/FAQ '
            + 'for more information.');
        const fallbackTranslation = icuTokens
            ? processICUString(originalString, langTag, icuTokens, icuOptions, stringKey)
            : originalString;
        return `≪≪${fallbackTranslation}≫≫`;
    }
    const finalString = translations[stringKey].value || originalString || stringKey;
    return icuTokens
        ? processICUString(finalString, langTag, icuTokens, icuOptions, stringKey)
        : finalString;
}
//# sourceMappingURL=get-translation.js.map